import React, { Suspense } from "react";
import {
  Button,
  Dialog,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  Link,
  Text,
} from "@fluentui/react-components";
import { DismissRegular } from "@fluentui/react-icons/svg/dismiss";
import { useConfig } from "@contexts/ConfigContext";
import { getSourceHelpContent } from "./SourceHelpRegistry";
import { useConnectionHelpPanelViewModel } from "./ConnectionHelpPanel.ViewModel";
import { useConnectionHelpPanelText } from "./ConnectionHelpPanel.i18n";
import type { ConnectionHelpPanelProps } from "./ConnectionHelpPanel.ViewModel.types";
import "./ConnectionHelpPanel.scss";

const SourceHelpContentRenderer: React.FC<{ sourceId: string }> = ({
  sourceId,
}) => {
  const HelpContent = getSourceHelpContent(sourceId);
  if (!HelpContent) return null;
  // eslint-disable-next-line react-hooks/static-components
  return <HelpContent sourceId={sourceId} />;
};

export const ConnectionHelpPanel: React.FC<ConnectionHelpPanelProps> = ({
  open,
  source,
  onDismiss,
}) => {
  const { config } = useConfig();
  const iconBaseUrl = config?.regionBaseUrl || "";
  const vm = useConnectionHelpPanelViewModel({ source });
  const text = useConnectionHelpPanelText();

  return (
    <Dialog
      open={open}
      modalType="non-modal"
      onOpenChange={(_, data) => {
        if (!data.open) onDismiss();
      }}
    >
      <DialogSurface className="connection-help-panel__surface">
        <DialogBody>
          <DialogTitle
            action={
              <Button
                appearance="subtle"
                icon={<DismissRegular />}
                aria-label={text.closeAriaLabel}
                onClick={onDismiss}
              />
            }
          >
            <div className="connection-help-panel__title">
              {!vm.hasRegisteredContent && (
                <img
                  className="connection-help-panel__icon"
                  src={`${iconBaseUrl}/assets/img/sourceicons/${source.id.toLowerCase()}.png`}
                  alt=""
                />
              )}
              {source.displayName}
            </div>
          </DialogTitle>

          <DialogContent className="connection-help-panel__content">
            {vm.hasRegisteredContent ? (
              <Suspense fallback={null}>
                <SourceHelpContentRenderer sourceId={source.id} />
              </Suspense>
            ) : (
              <>
                <Text weight="semibold" block>
                  {text.fallbackTitle}
                </Text>
                <Text size={200} block>
                  {text.fallbackBody}
                </Text>
              </>
            )}

            <Link
              className="connection-help-panel__docs-link"
              href={vm.docsUrl}
              target="_blank"
              rel="noreferrer"
            >
              {text.fullDocumentationLink}
            </Link>
          </DialogContent>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
};
